/**
 * MePregnancy — Phase 3 pregnancy home
 *   /me/health/pregnancy
 *   • Week/day counter · baby size · progress bar
 *   • Kick counter (10-kick session with timer)
 *   • Weekly tip
 *   • Feature tile grid
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import {
  ArrowLeft, Baby, Heart, Ruler, Scales, Calendar, Trash,
  ArrowRight, Timer, Warning, Play, Stop, Info, ForkKnife, Bell,
} from "@phosphor-icons/react";

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function prettyDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
  });
}
function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff}m ago`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function fmtMins(m) {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

// Emoji for baby size — matches the WEEKLY_DATA on backend.
const SIZE_EMOJI = {
  "Poppy seed": "🌱", "Sesame seed": "🌱", "Lentil": "🫘", "Blueberry": "🫐",
  "Kidney bean": "🫘", "Grape": "🍇", "Kumquat": "🍊", "Fig": "🍎", "Lime": "🟢",
  "Lemon": "🍋", "Peach": "🍑", "Apple": "🍎", "Avocado": "🥑", "Pear": "🍐",
  "Bell pepper": "🫑", "Mango": "🥭", "Banana": "🍌", "Carrot": "🥕", "Papaya": "🥭",
  "Grapefruit": "🍊", "Corn cob": "🌽", "Cauliflower": "🥦", "Lettuce": "🥬",
  "Coconut": "🥥", "Eggplant": "🍆", "Butternut sq.": "🎃", "Cabbage": "🥬",
  "Pineapple": "🍍", "Squash": "🎃", "Durian": "🥭", "Melon": "🍈",
  "Honeydew": "🍈", "Watermelon": "🍉", "Winter melon": "🍈", "Pumpkin": "🎃",
  "Small pumpkin": "🎃",
};

function BackHeader({ to, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <Link to={to} className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-50">
        <ArrowLeft size={16} weight="bold" className="text-gray-700"/>
      </Link>
      <div className="flex-1 min-w-0">
        <h1 className="font-display font-black text-[22px] leading-tight tracking-tight text-[#111827]">
          {title}
        </h1>
        {subtitle && <div className="text-[12px] text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Pregnancy home
// ══════════════════════════════════════════════════════════════════
export function PregnancyHome() {
  const [lovedOnes, setLovedOnes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [summary, setSummary] = useState(null);
  const [kicks, setKicks] = useState([]);
  const [showKickPad, setShowKickPad] = useState(false);

  const loadLO = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = r.data.loved_ones || [];
      setLovedOnes(list);
      const preg = list.find(l => l.kind === "pregnant") || list.find(l => l.kind === "woman");
      if (preg && !selectedId) setSelectedId(preg.id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const load = async () => {
    if (!selectedId) return;
    try {
      const [s, k] = await Promise.all([
        custApi.get(`/me/pregnancy/summary?loved_one_id=${selectedId}`),
        custApi.get(`/me/kicks?loved_one_id=${selectedId}&days=30`),
      ]);
      setSummary(s.data);
      setKicks(k.data.sessions || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadLO(); }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [selectedId]);

  const eligible = lovedOnes.filter(l => ["pregnant", "woman", "self"].includes(l.kind || "self"));

  if (eligible.length === 0) {
    return (
      <div className="space-y-4 px-4 py-4">
        <BackHeader to="/me/health" title="Pregnancy"/>
        <div className="border border-dashed border-[#EC4899] bg-[#FCE7F3] rounded-md p-4 text-center">
          <Baby size={24} weight="duotone" className="mx-auto mb-1 text-[#EC4899]"/>
          <div className="text-sm font-semibold text-[#1A1A1A]">Add yourself as Pregnant</div>
          <div className="text-[11px] text-[#5C5A55] mt-1">
            On Profile → Health Profile, set Care Kind to <b>Pregnant</b> and add your LMP (last-period date).
          </div>
          <Link to="/me/profile#health-profile"
                className="mt-3 inline-block h-9 px-4 leading-9 bg-[#EC4899] hover:bg-[#DB2777] text-white rounded-md text-xs font-medium">
            Go to Profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4 py-4 pb-8" data-testid="me-pregnancy">
      <BackHeader to="/me/health" title="Pregnancy"
                    subtitle="Week-by-week guidance"/>

      {/* Loved-one picker */}
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1.5 font-semibold">
          Whose pregnancy?
        </label>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
          {eligible.map(lo => {
            const on = lo.id === selectedId;
            return (
              <button key={lo.id} onClick={() => setSelectedId(lo.id)}
                      data-testid={`preg-picker-${lo.id}`}
                      className={`h-10 px-3 shrink-0 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                        on ? "bg-[#EC4899] text-white border-[#EC4899]"
                           : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                      }`}>
                <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                  on ? "bg-white/25" : "bg-[#FCE7F3] text-[#EC4899]"
                }`}>{lo.name?.[0]?.toUpperCase() || "?"}</span>
                {lo.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Hero */}
      {summary === null ? (
        <div className="text-xs text-gray-500 text-center py-6">Loading…</div>
      ) : !summary.active ? (
        <div className="border border-dashed border-[#EC4899] bg-[#FCE7F3] rounded-md p-4 text-center">
          <Baby size={24} weight="duotone" className="mx-auto mb-1 text-[#EC4899]"/>
          <div className="text-sm font-semibold text-[#1A1A1A]">Set LMP to unlock</div>
          <div className="text-[11px] text-[#5C5A55] mt-1">
            {summary.message || "Add the LMP date on the loved-one profile."}
          </div>
          <Link to="/me/profile#health-profile"
                className="mt-3 inline-block h-9 px-4 leading-9 bg-[#EC4899] hover:bg-[#DB2777] text-white rounded-md text-xs font-medium">
            Set LMP
          </Link>
        </div>
      ) : (
        <>
          {/* Hero card */}
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#FCE7F3] via-[#FBCFE8] to-[#FBB6CE] p-5 shadow-[0_20px_50px_rgba(236,72,153,0.20)]"
               data-testid="preg-hero">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-[#831843] font-medium">You are</div>
                <div className="font-display font-black text-[26px] leading-tight text-[#BE185D]">
                  {summary.week} Weeks{summary.day_in_week > 0 ? ` ${summary.day_in_week}d` : ""} Pregnant
                </div>
                <div className="text-[11px] text-[#831843] mt-0.5 font-medium">
                  T{summary.trimester} · {summary.days_to_go > 0 ? `${summary.days_to_go} days to go` : "due!"}
                </div>

                {/* Baby stats */}
                <div className="grid grid-cols-3 gap-2 mt-3">
                  <MiniStat Icon={Ruler} label="Size" value={summary.baby_size}/>
                  <MiniStat Icon={Scales} label="Weight"
                            value={summary.baby_weight_g >= 1000
                                    ? `${(summary.baby_weight_g / 1000).toFixed(1)}kg`
                                    : `${summary.baby_weight_g}g`}/>
                  <MiniStat Icon={Calendar} label="Due"
                            value={new Date(summary.due_date + "T00:00:00")
                                     .toLocaleDateString(undefined, {day:"numeric", month:"short"})}/>
                </div>

                {/* Progress */}
                <div className="mt-3">
                  <div className="flex items-center justify-between text-[10px] font-bold text-[#831843]">
                    <span>Pregnancy progress</span>
                    <span>{summary.progress_pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-white/60 overflow-hidden mt-1">
                    <div className="h-full bg-gradient-to-r from-[#EC4899] to-[#BE185D]"
                         style={{ width: `${summary.progress_pct}%` }}/>
                  </div>
                </div>
              </div>
              {/* Baby "emoji" bubble */}
              <div className="w-24 h-28 shrink-0 rounded-2xl bg-white/40 backdrop-blur inline-flex items-center justify-center text-6xl relative">
                {SIZE_EMOJI[summary.baby_size] || "🍼"}
                <div className="absolute -bottom-2 -right-1 h-6 px-2 rounded-full bg-white text-[10px] font-bold text-[#BE185D] inline-flex items-center shadow">
                  {summary.baby_length}
                </div>
              </div>
            </div>
          </div>

          {/* Weekly tip */}
          {summary.weekly_tip && (
            <div className="rounded-2xl p-4 bg-white border border-pink-100 flex gap-3"
                 data-testid="preg-tip">
              <div className="w-9 h-9 rounded-full bg-[#FCE7F3] text-[#EC4899] inline-flex items-center justify-center shrink-0">
                <Info size={16} weight="fill"/>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#EC4899]">
                  Week {summary.week} tip
                </div>
                <div className="text-[13px] text-[#1A1A1A] font-medium leading-snug mt-0.5">
                  {summary.weekly_tip}
                </div>
              </div>
            </div>
          )}

          {/* Feature grid — inspired by mockup */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold mb-2">
              Track & Manage
            </div>
            <div className="grid grid-cols-4 gap-2" data-testid="preg-features">
              <FeatureTile Icon={Heart} label="Kick Counter" color="#EC4899" bg="#FCE7F3"
                            onClick={() => setShowKickPad(true)}/>
              <FeatureTile Icon={Baby} label="Baby Size" color="#BE185D" bg="#FBCFE8"
                            to="#" onClick={() => toast.info("Weekly info shown in the tip card above 💗")}/>
              <FeatureTile Icon={Bell} label="Reminders" color="#7C3AED" bg="#EDE9FE"
                            to="/me/health/pills"/>
              <FeatureTile Icon={ForkKnife} label="Nutrition" color="#15803D" bg="#DCFCE7"
                            to="#" onClick={() => toast.info("Eat iron-rich foods: spinach, dates, jaggery, lentils, red meat")}/>
              <FeatureTile Icon={Calendar} label="Appointments" color="#2563EB" bg="#DBEAFE"
                            to="/me/health/bookings"/>
              <FeatureTile Icon={Scales} label="Weight" color="#EA580C" bg="#FFEDD5"
                            to="/me/health/vitals"/>
              <FeatureTile Icon={Timer} label="Contractions" color="#DC2626" bg="#FEE2E2"
                            onClick={() => setShowKickPad("contractions")}/>
              <FeatureTile Icon={Warning} label="SOS" color="#7F1D1D" bg="#FEE2E2"
                            to="/me/health/emergency"/>
            </div>
          </div>

          {/* Kick counter pad */}
          {showKickPad && (
            <KickPad lovedOneId={selectedId}
                     mode={showKickPad === "contractions" ? "contractions" : "kicks"}
                     onClose={() => { setShowKickPad(false); load(); }}/>
          )}

          {/* Recent kick sessions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.22em] text-gray-400 font-bold">
                Kick log ({kicks.length})
              </div>
              <button onClick={() => setShowKickPad(true)}
                      data-testid="preg-kick-open"
                      className="text-[11px] font-bold text-[#EC4899]">+ Log kicks</button>
            </div>
            {kicks.length === 0 ? (
              <div className="border border-dashed border-[#E5E2D9] rounded-md p-6 text-center">
                <Heart size={22} color="#EC4899" className="mx-auto mb-1"/>
                <div className="text-xs text-[#5C5A55]">
                  Tap the Kick Counter tile after 28 weeks to log baby movement.
                </div>
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="preg-kick-list">
                {kicks.slice(0, 8).map(k => (
                  <KickRow key={k.id} row={k} onDelete={async () => {
                    if (!window.confirm("Delete this kick session?")) return;
                    await custApi.delete(`/me/kicks/${k.id}`);
                    load();
                  }}/>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MiniStat({ Icon, label, value }) {
  return (
    <div className="bg-white/60 backdrop-blur rounded-xl p-1.5">
      <Icon size={12} weight="fill" className="text-[#EC4899]"/>
      <div className="text-[9px] uppercase tracking-widest font-bold text-[#831843] mt-0.5">{label}</div>
      <div className="text-[13px] font-black text-[#BE185D] leading-tight">{value}</div>
    </div>
  );
}

function FeatureTile({ Icon, label, color, bg, to, onClick }) {
  const inner = (
    <div className="bg-white rounded-2xl p-3 border border-gray-100 shadow-[0_4px_12px_rgba(0,0,0,0.03)] hover:shadow-md hover:border-[#EC4899] active:scale-[0.97] transition-all text-center">
      <div className="w-10 h-10 rounded-2xl inline-flex items-center justify-center mx-auto"
           style={{ backgroundColor: bg, color }}>
        <Icon size={18} weight="duotone"/>
      </div>
      <div className="text-[11px] font-semibold text-[#1A1A1A] mt-1.5 leading-tight">{label}</div>
    </div>
  );
  if (onClick) {
    return <button onClick={onClick} data-testid={`preg-tile-${label.toLowerCase().replace(/ /g,"-")}`}>{inner}</button>;
  }
  return <Link to={to} data-testid={`preg-tile-${label.toLowerCase().replace(/ /g,"-")}`}>{inner}</Link>;
}

function KickRow({ row, onDelete }) {
  return (
    <div className="flex items-center gap-3 border border-[#E5E2D9] rounded-md p-2.5"
         data-testid={`preg-kick-${row.id}`}>
      <div className="w-9 h-9 rounded-full bg-[#FCE7F3] text-[#EC4899] inline-flex items-center justify-center shrink-0">
        <Heart size={14} weight="fill"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[#1A1A1A]">
          {row.kicks} kicks
          <span className="ml-1.5 text-[10px] text-gray-500">in {fmtMins(row.duration_min)}</span>
        </div>
        <div className="text-[10px] text-gray-500">{timeAgo(row.started_at)}</div>
      </div>
      {row.kicks >= 10 && row.duration_min <= 60 && (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#DCFCE7] text-[#15803D] font-bold">HEALTHY</span>
      )}
      <button onClick={onDelete} className="p-1.5 rounded hover:bg-[#FEE2E2]">
        <Trash size={12} color="#DC2626"/>
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Kick counter pad — tap-once-per-kick session
// Standard obstetric guideline: 10 movements in under an hour = healthy.
// ══════════════════════════════════════════════════════════════════
function KickPad({ lovedOneId, mode, onClose }) {
  const [count, setCount] = useState(0);
  const [running, setRunning] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const startedRef = useRef(new Date());

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed(Math.round((Date.now() - startedRef.current.getTime()) / 1000)), 1000);
    return () => clearInterval(t);
  }, [running]);

  const durationMin = Math.max(1, Math.ceil(elapsed / 60));
  const done = mode === "kicks" && count >= 10;
  const heartRate = mode === "contractions" ? "≥ 1 in 10 min" : "10 in 1 hour";

  const save = async () => {
    if (count === 0) return onClose();
    try {
      await custApi.post("/me/kicks/log", {
        loved_one_id: lovedOneId,
        kicks: count,
        duration_min: durationMin,
        started_at: startedRef.current.toISOString(),
        notes: mode === "contractions" ? "contractions" : null,
      });
      toast.success("Session saved");
    } catch (e) { toast.error(formatErr(e)); }
    onClose();
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div className="rounded-3xl p-5 bg-gradient-to-br from-[#EC4899] via-[#DB2777] to-[#831843] text-white shadow-[0_20px_50px_rgba(219,39,119,0.30)] relative"
         data-testid="preg-kickpad">
      <button onClick={save}
              data-testid="preg-kickpad-close"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 inline-flex items-center justify-center">
        ✕
      </button>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">
        {mode === "contractions" ? "Contraction timer" : "Kick counter"}
      </div>
      <div className="text-[11px] opacity-85 mt-0.5">Healthy target: {heartRate}</div>

      {/* Big tap area */}
      <button onClick={() => setCount(c => c + 1)}
              data-testid="preg-kick-tap"
              className="w-full mt-4 rounded-2xl bg-white/15 backdrop-blur hover:bg-white/20 active:scale-[0.98] transition-transform py-8 text-center">
        <Heart size={40} weight="fill" className="mx-auto"/>
        <div className="font-display font-black text-6xl mt-1 leading-none">{count}</div>
        <div className="text-[11px] mt-2 opacity-90 uppercase tracking-widest font-bold">
          Tap for every {mode === "contractions" ? "contraction" : "kick"}
        </div>
      </button>

      {/* Elapsed + status */}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div className="bg-white/15 rounded-xl p-2 text-center">
          <div className="text-[10px] uppercase tracking-widest opacity-85 font-bold">Elapsed</div>
          <div className="font-mono font-black text-lg">{mm}:{ss}</div>
        </div>
        <div className={`rounded-xl p-2 text-center ${done ? "bg-[#DCFCE7] text-[#15803D]" : "bg-white/15 text-white"}`}>
          <div className="text-[10px] uppercase tracking-widest font-bold opacity-85">Status</div>
          <div className="text-sm font-bold">{done ? "10 reached ✓" : "In progress"}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-3">
        <button onClick={() => setRunning(r => !r)}
                data-testid="preg-kick-pause"
                className="h-10 rounded-xl bg-white/20 hover:bg-white/25 text-white font-semibold text-sm inline-flex items-center justify-center gap-1.5">
          {running ? <><Stop size={13} weight="fill"/> Pause</> : <><Play size={13} weight="fill"/> Resume</>}
        </button>
        <button onClick={save}
                data-testid="preg-kick-save"
                className="h-10 rounded-xl bg-white text-[#BE185D] font-semibold text-sm">
          Save &amp; close
        </button>
      </div>
    </div>
  );
}
